"use client";

import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { Loader, Upload, UserCog } from "lucide-react";
import { toast } from "sonner";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { AvatarCropper } from "@/components/account/AvatarCropper";
import { useAuth } from "@/context/AuthContext";
import { useTitleBar } from "@/context/TitleBarProvider";
import { useChangePassword, useUpdateAvatar, useUpdateProfile } from "@/hooks/useAccount";

const MAX_AVATAR_BYTES = 3 * 1024 * 1024;

const profileSchema = z.object({
  first_name: z.string().max(255),
  last_name: z.string().max(255),
});
type ProfileValues = z.infer<typeof profileSchema>;

const passwordSchema = z
  .object({
    current_password: z.string().min(1, "Enter your current password"),
    new_password: z.string().min(8, "At least 8 characters"),
    confirm_password: z.string().min(1, "Confirm your new password"),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    path: ["confirm_password"],
    message: "Passwords do not match",
  });

type PasswordValues = z.infer<typeof passwordSchema>;

function apiError(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    const detail = error.response?.data as { error?: string } | undefined;
    if (detail?.error) return detail.error;
  }
  return fallback;
}

function getInitials(name: string, email: string) {
  const source = name?.trim() || email?.trim() || "";
  const [first, second] = source.split(/[\s@.]+/);
  return `${first?.charAt(0) ?? ""}${second?.charAt(0) ?? ""}`.toUpperCase();
}

export default function AccountPage() {
  const { setTitle, setIcon, setButtons } = useTitleBar();
  const { user, refreshUser } = useAuth();

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = React.useState<string | null>(null);
  const [preview, setPreview] = React.useState<string | null>(null);

  const avatarMutation = useUpdateAvatar();
  const passwordMutation = useChangePassword();
  const profileMutation = useUpdateProfile();

  const profileForm = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { first_name: "", last_name: "" },
  });

  // Prefill the name form once the user (and any later refresh) has loaded.
  React.useEffect(() => {
    profileForm.reset({
      first_name: user?.first_name ?? "",
      last_name: user?.last_name ?? "",
    });
  }, [user?.first_name, user?.last_name, profileForm]);

  const form = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { current_password: "", new_password: "", confirm_password: "" },
  });

  const handleSaveProfile = (values: ProfileValues) => {
    profileMutation.mutate(values, {
      onSuccess: async () => {
        await refreshUser();
        toast.success("Name updated");
      },
      onError: (error) => toast.error(apiError(error, "Failed to update name")),
    });
  };

  React.useEffect(() => {
    setTitle("Account");
    setIcon(<UserCog size={24} />);
    setButtons(null);
  }, [setTitle, setIcon, setButtons]);

  const shownAvatar = preview ?? user?.avatar ?? undefined;

  const handlePickFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error("Image is too large (max 3 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result as string);
    reader.onerror = () => toast.error("Could not read that image");
    reader.readAsDataURL(file);
  };

  const handleSaveAvatar = () => {
    if (!preview) return;
    avatarMutation.mutate(preview, {
      onSuccess: async () => {
        await refreshUser();
        setPreview(null);
        toast.success("Avatar updated");
      },
      onError: (error) => toast.error(apiError(error, "Failed to update avatar")),
    });
  };

  const handleRemoveAvatar = () => {
    avatarMutation.mutate(null, {
      onSuccess: async () => {
        await refreshUser();
        setPreview(null);
        toast.success("Avatar removed");
      },
      onError: (error) => toast.error(apiError(error, "Failed to remove avatar")),
    });
  };

  const handleChangePassword = (values: PasswordValues) => {
    passwordMutation.mutate(
      { current_password: values.current_password, new_password: values.new_password },
      {
        onSuccess: () => {
          toast.success("Password changed");
          form.reset();
        },
        onError: (error) => toast.error(apiError(error, "Failed to change password")),
      },
    );
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <h1 className="text-2xl font-bold text-stone-700 md:hidden">Account</h1>

      {/* Display name */}
      <Card>
        <CardHeader>
          <CardTitle>Name</CardTitle>
          <CardDescription>
            Your display name, stored in Keycloak — so it shows across Bitswan and stays
            in sync with any connected SSO identity provider.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...profileForm}>
            <form
              onSubmit={(event) => void profileForm.handleSubmit(handleSaveProfile)(event)}
              className="flex max-w-md flex-col gap-4"
            >
              <div className="flex flex-col gap-4 sm:flex-row">
                <FormField
                  control={profileForm.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>First name</FormLabel>
                      <FormControl>
                        <Input autoComplete="given-name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={profileForm.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Last name</FormLabel>
                      <FormControl>
                        <Input autoComplete="family-name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div>
                <Button type="submit" disabled={profileMutation.isPending}>
                  {profileMutation.isPending && <Loader size={16} className="mr-2 animate-spin" />}
                  Save name
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Profile picture */}
      <Card>
        <CardHeader>
          <CardTitle>Profile picture</CardTitle>
          <CardDescription>
            This avatar appears next to your name across the workspace.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            className="hidden"
            onChange={handlePickFile}
          />

          {cropSrc ? (
            <AvatarCropper
              src={cropSrc}
              busy={avatarMutation.isPending}
              onCancel={() => setCropSrc(null)}
              onCropped={(dataUrl) => {
                setPreview(dataUrl);
                setCropSrc(null);
              }}
            />
          ) : (
            <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
              <Avatar className="h-24 w-24 rounded-full">
                <AvatarImage src={shownAvatar} alt="Avatar" />
                <AvatarFallback className="rounded-full text-2xl">
                  {getInitials(user?.name ?? "", user?.email ?? "")}
                </AvatarFallback>
              </Avatar>

              <div className="flex flex-col gap-3">
                {preview ? (
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" onClick={handleSaveAvatar} disabled={avatarMutation.isPending}>
                      {avatarMutation.isPending && <Loader size={16} className="mr-2 animate-spin" />}
                      Save photo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCropSrc(preview)}
                      disabled={avatarMutation.isPending}
                    >
                      Re-crop
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setPreview(null)}
                      disabled={avatarMutation.isPending}
                    >
                      Discard
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={avatarMutation.isPending}
                    >
                      <Upload size={16} className="mr-2" />
                      {user?.avatar ? "Change photo" : "Upload photo"}
                    </Button>
                    {user?.avatar && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={handleRemoveAvatar}
                        disabled={avatarMutation.isPending}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">PNG, JPEG, GIF or WebP. Max 3&nbsp;MB.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Password */}
      <Card>
        <CardHeader>
          <CardTitle>Change password</CardTitle>
          <CardDescription>Signed in as {user?.email}.</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={(event) => void form.handleSubmit(handleChangePassword)(event)}
              className="flex max-w-sm flex-col gap-4"
            >
              <FormField
                control={form.control}
                name="current_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current password</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="current-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="new_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New password</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirm_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm new password</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div>
                <Button type="submit" disabled={passwordMutation.isPending}>
                  {passwordMutation.isPending && <Loader size={16} className="mr-2 animate-spin" />}
                  Update password
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
